import { msg } from '@lingui/core/macro';
import { i18n } from '@/i18n';
import { GEOLOCATION_CONFIG } from '@/config/constants';

export interface PermissionStatus {
  granted: boolean;
  camera: boolean;
  location: boolean;
  /** The camera is refused although the site is allowed: Chrome's own Android permission is off. */
  cameraOffInPhoneSettings: boolean;
}

/**
 * Detects if the current device is running iOS (iPhone, iPad, iPod)
 * @returns true if iOS device, false otherwise
 */
export const isIOSDevice = (): boolean => {
  const userAgent = navigator.userAgent.toLowerCase();
  return /iphone|ipad|ipod/.test(userAgent);
};

/*
 * Only a refusal counts as missing. After Android restored Chrome, a camera
 * still held by the camera app or a slow first fix used to show the user
 * "camera and location missing" while both were granted.
 *
 * Always probe, even when navigator.permissions says granted: that reports
 * Chrome's site setting, and stays "granted" for the camera after Chrome's
 * own Android camera permission is turned off (Android 15 emulator).
 */

/** What the browser has on record, without prompting. False where it cannot say. */
async function recordedGranted(name: 'camera' | 'geolocation'): Promise<boolean> {
  try {
    return (await navigator.permissions.query({ name: name as PermissionName })).state === 'granted';
  } catch {
    return false;
  }
}

/**
 * A refused camera whose site permission still reads granted. Only Chrome's
 * Android permission (Settings → Apps → Chrome → Permissions) causes that, and
 * once Android stops asking, only that setting brings the camera back.
 */
export async function cameraOffInPhoneSettings(error: unknown): Promise<boolean> {
  return (error as Error | undefined)?.name === 'NotAllowedError' && (await recordedGranted('camera'));
}

/** Where to turn Chrome's camera permission back on. */
export const cameraOffInPhoneSettingsMessage = (): string =>
  i18n._(
    msg`Chrome does not have camera permission on this phone. Open Settings → Apps → Chrome → Permissions → Camera and choose Allow.`
  );

async function probeCamera(): Promise<{ allowed: boolean; offInPhoneSettings: boolean }> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    // Stop the stream immediately, we just needed to trigger the permission request
    stream.getTracks().forEach((track) => track.stop());
    return { allowed: true, offInPhoneSettings: false };
  } catch (error) {
    const name = (error as Error | undefined)?.name;
    if (name !== 'NotAllowedError' && name !== 'SecurityError') return { allowed: true, offInPhoneSettings: false };
    return { allowed: false, offInPhoneSettings: await cameraOffInPhoneSettings(error) };
  }
}

/**
 * Unlike the camera, Chrome's geolocation record follows its Android permission
 * too (it read "prompt" with that off), so "granted" can be trusted and the wait
 * for a fix, up to 15 s on a slow phone, skipped.
 */
async function probeLocation(): Promise<boolean> {
  if (await recordedGranted('geolocation')) return true;
  try {
    await new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 15000, enableHighAccuracy: false });
    });
    return true;
  } catch (error) {
    // A timeout or no fix is not a refusal; the capture asks for the fix again.
    return (error as GeolocationPositionError | undefined)?.code !== 1;
  }
}

/** Camera and location permissions for photo capture. The probes show the prompts; only a refusal is missing. */
export const requestPermissions = async (): Promise<PermissionStatus> => {
  const camera = await probeCamera();
  const location = GEOLOCATION_CONFIG.ENABLED ? await probeLocation() : true;
  return {
    granted: camera.allowed && location,
    camera: camera.allowed,
    location,
    cameraOffInPhoneSettings: camera.offInPhoneSettings,
  };
};
